import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  useTheme,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Divider,
  styled,
  Box,
  Button,
  Link
} from "@mui/material";
import { ArrowForward, LocationOn, Work, People } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import ExhibitorModal from "./ExhibitorModal";
import SponsorModal from "./SponsorModal";

const StyledCard = styled(Card)(({ theme }) => ({
  width: "100%",
  height: "100%",
  position: "relative",
  overflow: "visible",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  cursor: "pointer",
  '&:hover': {
    transform: "translateY(-8px)",
    boxShadow: theme.shadows[6],
  }
}));

const SponsorCard = ({ refresh, exhibitor, exhibitors, role, renderActions }) => {
  const theme = useTheme();
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation();

  // Parse team members from stringified array
  const teamMembers = JSON.parse(exhibitor.team_members || "[]");

  return (
    <>
      <StyledCard onClick={() => setOpenModal(true)}>
        <CardContent sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.shadows[3],
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
          p: 2
        }}>
          <Box
            component="img"
            src={exhibitor.profile_pic || './profile_pic.png'}
            alt={exhibitor.company_name}
            sx={{
              width: '100%',
              height: 200,
              objectFit: 'contain',
              mx: "auto",
              mb: 2,
              borderRadius: theme.shape.borderRadius,
            }}
          />

          <Typography variant="h6" textAlign="center" fontWeight="600">
            {exhibitor.company_name}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            {exhibitor.stallNo && (
              <Chip
                label={`${t('StallNo')}: ${exhibitor.stallNo}`}
                color="primary"
                size="small"
              />
            )}
            {exhibitor.sponsorship_level && (
              <Chip
                label={exhibitor.sponsorship_level}
                color="secondary"
                size="small"
              />
            )}
          </Box>

          <Divider sx={{ width: '80%', my: 2 }} />

          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOn fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {exhibitor.country}
                </Typography>
              </Box>
              
              {exhibitor.website && (
                <Link 
                  href={exhibitor.website} 
                  target="_blank" 
                  rel="noopener"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <Work fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="body2">
                    {t('Website')}
                  </Typography>
                </Link>
              )}
            </Box>

          </Box>

          {renderActions && (
            <Box sx={{ mt: 'auto', width: '100%' }} onClick={(e) => e.stopPropagation()}>
              {renderActions(exhibitor)}
            </Box>
          )}
        </CardContent>
      </StyledCard>

      <SponsorModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        exhibitor={exhibitor}
        exhibitors={exhibitors}
        refresh={refresh}
        role={role}
      />
    </>
  );
};

SponsorCard.propTypes = {
  exhibitor: PropTypes.shape({
    id: PropTypes.string.isRequired,
    company_name: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    stallNo: PropTypes.string,
    sponsorship_level: PropTypes.string,
    profile_pic: PropTypes.string,
    company_profile: PropTypes.string,
    website: PropTypes.string,
    team_members: PropTypes.string,
    isConnected: PropTypes.string,
  }).isRequired,
  exhibitors: PropTypes.array.isRequired,
  refresh: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  renderActions: PropTypes.func,
};

export default SponsorCard;